import {
    createHashHistory,
    Outlet,
    ReactLocation,
    Router,
    useLocation,
} from '@tanstack/react-location';
import Balance from '../../balance/components/Balance';
import BankingPage from '../../banking/components/BankingPage';
import BookingView from '../../booking/components/BookingView';
import CalendarPage from '../../calendar/components/CalendarPage';
import CheckinPage from '../../checkin/components/CheckinPage';
import Cleaning from '../../cleaning/component/Cleaning';
import ExpensesPage from '../../expense/components/ExpensesPage';
import FinancialDashboard from '../../financial/components/FinancialDashboard';
import TaxPaymentsPage from '../../taxPayments/components/TaxPaymentsPage';
import Home from '../../home/components/Home';
import LockboxCodeManagement from '../../lockbox/components/LockboxCodeManagement';
import PropertyManagementPage from '../../property/components/PropertyManagementPage';
import PropertySettingsLayout from '../../property/pages/PropertySettingsLayout';
import GeneralSettingsPage from '../../property/pages/GeneralSettingsPage';
import AccessSettingsPage from '../../property/pages/AccessSettingsPage';
import SharingSettingsPage from '../../property/pages/SharingSettingsPage';
import ModulesSettingsPage from '../../property/pages/ModulesSettingsPage';
import SettingsPage from '../../settings/components/SettingsPage';
import StatsHeatmap from '../../stats/components/StatsHeatmap';
import StatsMonthly from '../../stats/components/StatsMonthly';
import StatsStatistics from '../../stats/components/StatsStatistics';
import TaxBreakdown from '../../taxes/components/TaxBreakdown';
import TouristTaxRedirect from '../../taxes/components/TouristTaxRedirect';
import UsersPage from '../../users/components/UsersPage';
import AcceptInvitation from './AcceptInvitation';
import ForgotPassword from './ForgotPassword';
import LoginForm from './LoginForm';
import ResetPassword from './ResetPassword';
import StatsLayout from './StatsLayout';
import Layout from './common/layout/Layout';

const history = createHashHistory();

const location = new ReactLocation({ history });

// Routes that don't require authentication
const UNAUTHENTICATED_ROUTES = ['/accept-invitation', '/forgot-password', '/reset-password'];

// Inner component that uses useLocation for reactive route checking
const AppContent = () => {
    const currentLocation = useLocation();
    const currentPath = currentLocation.current.pathname;
    const isUnauthenticatedRoute = UNAUTHENTICATED_ROUTES.some(route => currentPath.startsWith(route));
    const hasToken = localStorage.getItem('token');

    return (
        <Layout>
            {isUnauthenticatedRoute && <Outlet />}
            {!isUnauthenticatedRoute && !hasToken && <LoginForm />}
            {!isUnauthenticatedRoute && hasToken && <Outlet />}
        </Layout>
    );
};

const App = () => {

    return (
        <Router
            routes={[
                {
                    path: '/accept-invitation',
                    element: <AcceptInvitation />,
                },
                {
                    path: '/forgot-password',
                    element: <ForgotPassword />,
                },
                {
                    path: '/reset-password',
                    element: <ResetPassword />,
                },
                {
                    path: '/properties',
                    element: <PropertyManagementPage />,
                },
                {
                    path: '/users',
                    element: <UsersPage />,
                },
                {
                    path: '/settings',
                    element: <SettingsPage />,
                },
                // Property-scoped routes (with propertyId)
                {
                    path: '/:propertyId/booking/:id',
                    element: <BookingView />,
                },
                {
                    path: '/:propertyId/cleaning',
                    element: <Cleaning />,
                },
                {
                    path: '/:propertyId/banking',
                    element: <BankingPage />,
                },
                {
                    path: '/:propertyId/balance',
                    element: <Balance />,
                },
                {
                    path: '/:propertyId/taxes',
                    element: <TaxBreakdown />,
                },
                {
                    path: '/:propertyId/tourist-tax',
                    element: <TouristTaxRedirect />,
                },
                {
                    path: '/:propertyId/stats',
                    element: <StatsLayout />,
                    children: [
                        {
                            path: '/',
                            element: <StatsHeatmap />,
                        },
                        {
                            path: '/heatmap',
                            element: <StatsHeatmap />,
                        },
                        {
                            path: '/monthly',
                            element: <StatsMonthly />,
                        },
                        {
                            path: '/statistics',
                            element: <StatsStatistics />,
                        },
                    ],
                },
                {
                    path: '/:propertyId/settings',
                    element: <PropertySettingsLayout />,
                    children: [
                        {
                            path: '/',
                            element: <GeneralSettingsPage />,
                        },
                        {
                            path: '/general',
                            element: <GeneralSettingsPage />,
                        },
                        {
                            path: '/access',
                            element: <AccessSettingsPage />,
                        },
                        {
                            path: '/sharing',
                            element: <SharingSettingsPage />,
                        },
                        {
                            path: '/modules',
                            element: <ModulesSettingsPage />,
                        },
                    ],
                },
                {
                    path: '/:propertyId/lockbox',
                    element: <LockboxCodeManagement />,
                },
                {
                    path: '/:propertyId/calendar',
                    element: <CalendarPage />,
                },
                {
                    path: '/:propertyId/financial/tax-payments',
                    element: <TaxPaymentsPage />,
                },
                {
                    path: '/:propertyId/financial',
                    element: <FinancialDashboard />,
                },
                {
                    path: '/:propertyId/expenses',
                    element: <ExpensesPage />,
                },
                {
                    path: '/:propertyId/checkin/:id',
                    element: <CheckinPage />,
                },
                // Legacy routes (without propertyId) - trigger property selection
                {
                    path: '/booking/:id',
                    element: <BookingView />,
                },
                {
                    path: '/checkin/:id',
                    element: <CheckinPage />,
                },
                {
                    path: '/cleaning',
                    element: <Cleaning />,
                },
                {
                    path: '/banking',
                    element: <BankingPage />,
                },
                {
                    path: '/balance',
                    element: <Balance />,
                },
                {
                    path: '/taxes',
                    element: <TaxBreakdown />,
                },
                {
                    path: '/tourist-tax',
                    element: <TouristTaxRedirect />,
                },
                {
                    path: '/stats',
                    element: <StatsLayout />,
                    children: [
                        {
                            path: '/',
                            element: <StatsHeatmap />,
                        },
                        {
                            path: '/heatmap',
                            element: <StatsHeatmap />,
                        },
                        {
                            path: '/monthly',
                            element: <StatsMonthly />,
                        },
                        {
                            path: '/statistics',
                            element: <StatsStatistics />,
                        },
                    ],
                },
                {
                    path: '/lockbox',
                    element: <LockboxCodeManagement />,
                },
                {
                    path: '/calendar',
                    element: <CalendarPage />,
                },
                {
                    path: '/financial/tax-payments',
                    element: <TaxPaymentsPage />,
                },
                {
                    path: '/financial',
                    element: <FinancialDashboard />,
                },
                {
                    path: '/expenses',
                    element: <ExpensesPage />,
                },
                // Property home route
                {
                    path: '/:propertyId/home',
                    element: <Home />,
                },
                // Root route - shows property selection
                {
                    path: '/',
                    element: <Home />,
                },
            ]}
            location={location}
        >
            <AppContent />
        </Router>
    );
};

export default App;
